'use client';

import { useMemo } from 'react';
import { styled } from '@linaria/react';
import { useQuery } from '@tanstack/react-query';
import { GETv2Search } from '@fe-news/api/news';
import AsideSection from '../AsideSection/AsideSection';
import { CustomEventName, GTMEventKey } from '@fe-cnyes/fe-common-gtm';
import { GTMConstants } from '@fe-news/constants/pop-topic/type';
import Image from 'next/image';
import SmartTime from '@fe-common-ui/SmartTime/SmartTime';
import WrappedSkeleton from '@fe-news/components/atoms/WrappedSkeleton';
import ErrorReloadBlock from '@fe-news/components/atoms/ErrorReloadBlock';
import Link from 'next/link';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';

const bookmarkIcon = '/assets/icons/bookmark.webp';
const joeAvatar = '/assets/icons/joe-avatar.webp';

type CoverImage = {
  src: string;
  width: number;
  height: number;
  size: string;
};

type Category = {
  name: string;
  categoryId: number;
  slug: string;
};

type NewsResponse = {
  items: {
    total: 5;
    per_page: 20;
    current_page: 1;
    last_page: 1;
    next_page_url: null;
    prev_page_url: null;
    from: 1;
    to: 5;
    data: {
      coverSrc: Record<string, CoverImage>;
      payment: number;
      keyword: string[];
      newsId: number;
      title: string;
      content: string;
      publishAt: number;
      category: Category[];
      titleWithKwd: number;
      contentWithKwd: number;
      hasCoverPhoto: number;
    }[];
  };
  message: string;
  statusCode: number;
};

const ContentWrapper = styled.div<{ isNewsDetail?: boolean }>`
  background-color: white;
  border-radius: 6px;
  margin-top: 12px;
  border: solid 1px #e2e8f1;
  height: 113px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-left: ${({ isNewsDetail }) => (isNewsDetail ? '16px' : '0px')};
  margin-right: ${({ isNewsDetail }) => (isNewsDetail ? '16px' : '0px')};
  &::before {
    content: '';
    display: inline-block;
    width: 30px;
    height: 32px;
    position: absolute;
    top: -8px;
    left: 6px;
    background-image: url(${bookmarkIcon});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 1;
  }
`;

const AvatarWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 16px;
  border: solid 1px #e3e3e3;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const TextWrapper = styled.div`
  max-width: 230px;
`;

const Title = styled.div`
  display: flex;
  margin-bottom: 10px;
  & .card-title {
    color: #e03f19;
    padding-right: 9px;
    margin-right: 9px;
    position: relative;
    letter-spacing: 0.5px;

    &::after {
      content: '';
      display: inline-block;
      width: 2px;
      height: 16px;
      background-color: #e2e8f1;
      position: absolute;
      right: -1px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  & .date {
    color: #777;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TextContent = styled.div`
  width: 100%;

  & p {
    font-weight: 500;
    color: #383838;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 1.5;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const NewsLink = styled(Link)`
  color: #000000;
  &:hover {
    color: #e03f19;
  }
`;

const EmptyBlock = styled.div`
  width: 100%;
  height: 157px;
  margin-bottom: 16px;
  margin-top: 32px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: white;
  border: 1px solid #e2e8f1;
  border-radius: 6px;
`;

const params = { q: 'JoeLU', page: 1 };

interface JoeWallStreetColumnProps {
  isNewsDetail?: boolean;
}

const AsidePopTopic = ({ isNewsDetail = false }: JoeWallStreetColumnProps) => {
  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`);
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['joe-wall-street-column'],
    queryFn: () => GETv2Search(params) as Promise<NewsResponse>,
    select: data => data?.items?.data?.[0] ?? null
  });
  const showSkeleton = useMemo(() => {
    // 第一次載入時，顯示 Skeleton
    if (isLoading) return true;
    // 手動刷新時，顯示 Skeleton
    if (isError && isFetching) return true;
    return false;
  }, [isLoading, isError, data, isFetching]);

  const title = useMemo(() => {
    // 移除新聞標題【】內的文字 含【】
    return data?.title?.replace(/【[^】]*】/, '')?.trim() ?? '';
  }, [data]);

  const onReload = () => {
    refetch();
  };

  return isDesktop && isNewsDetail ? null : showSkeleton ? (
    <WrappedSkeleton
      height='157px'
      wrapperProps={{ style: { marginBottom: '16px', marginTop: '32px' } }}
      skeletonProps={{ borderRadius: 6 }}
    />
  ) : isError ? (
    <ErrorReloadBlock height='157px' onReload={onReload} />
  ) : data == null ? (
    <EmptyBlock />
  ) : (
    <AsideSection
      title='專家專欄'
      icon='/assets/icons/icon-joe-wall-street.svg'
      readMoreLink='https://hao.cnyes.com/ch/361680'
      readMoreTarget='_self'
      gtmEvent={{
        [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
        [GTMEventKey.SECTION]: `%p_${GTMConstants.JOE_WALL_STREET_COLUMN}`,
        [GTMEventKey.CLICK_ITEM]: '看更多'
      }}
      margin={isNewsDetail ? '40px 0px' : ''}
    >
      <ContentWrapper isNewsDetail={isNewsDetail}>
        <AvatarWrapper>
          <Image src={joeAvatar} alt='joe-avatar' width={60} height={60} />
        </AvatarWrapper>
        <TextWrapper>
          <Title>
            <h3 className='card-title'>Joe's華爾街脈動</h3>
            <span className='date'>
              <SmartTime second={data.publishAt} format='MM/DD' />
            </span>
          </Title>
          <TextContent>
            <NewsLink href={`/news/id/${data.newsId}`}>
              <p>{title}</p>
            </NewsLink>
          </TextContent>
        </TextWrapper>
      </ContentWrapper>
    </AsideSection>
  );
};

export default AsidePopTopic;
